import { Button, Card, Col, Form, message, Row, Space } from 'antd';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { CheckOutlined } from '@ant-design/icons';

import { useQuery } from '../../../shared/hooks/use-query';
import { CommonQueryParamKey } from '../../../shared/models/common-query-params';
import { Feature } from '../../../shared/models/features';
import { UserUpsertForm, UserUpsertFormValues } from '../../components/user-upsert-form';
import { translationNamespace } from '../../constants/translation-resources';
import { convertFormValuesToUser } from '../../data/converters/user-converters';
import { useCreateUserMutation } from '../../data/mutations/user-mutations';
import { NewResourceQueryParamKey } from '../../models/params';
import { AppResourcePaths } from '../../../shared/constants/app-resources';

export const UserNewView: FC = () => {
  const { t } = useTranslation(translationNamespace);
  const { push: routerPush } = useHistory();
  const [form] = Form.useForm<UserUpsertFormValues>();
  const query = useQuery();
  const mutation = useCreateUserMutation();

  const parsedPrev = query.get(CommonQueryParamKey.PREV);
  const prevUrl = parsedPrev ?? AppResourcePaths.users;
  const preselectedTenantUrlParam: string | null = query.get(NewResourceQueryParamKey.TENANT);
  const preselectedTenant: number | undefined =
    preselectedTenantUrlParam === null ? undefined : Number(preselectedTenantUrlParam);

  const submit = (values: UserUpsertFormValues) => {
    const newUser = convertFormValuesToUser(values);

    mutation.mutate(newUser, {
      onSuccess: () => {
        message.success(t('users.shared.createSuccessMessage'));
        routerPush(parsedPrev ?? `${AppResourcePaths.users}`);
      },
    });
  };

  const triggerSubmit = () => {
    form.submit();
  };

  useEffect(() => {
    if (preselectedTenant !== undefined) {
      form.setFieldsValue({ tenants: [preselectedTenant] });
    }
  }, [form, preselectedTenant]);

  return (
    <Row gutter={[16, 24]}>
      <Col span={24}>
        <Card title={t(`${Feature.SHARED}:details`)}>
          <UserUpsertForm form={form} onSubmit={submit} preselectedTenant={preselectedTenant} />
        </Card>
      </Col>
      <Col>
        <Space>
          <Button type="primary" htmlType="submit" size="large" icon={<CheckOutlined />} onClick={triggerSubmit}>
            {t(`${Feature.SHARED}:create`)}
          </Button>
          <Link to={prevUrl}>
            <Button htmlType="button" size="large">
              {t(`${Feature.SHARED}:cancel`)}
            </Button>
          </Link>
        </Space>
      </Col>
    </Row>
  );
};
